import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { deleteProductClaimMutationOptions } from '@/features/product-claim/api/delete-product-claim.api';
import { GET_LIST_KLAIM_PRODUK_QUERY_KEY } from '@/features/product-claim/api/get-list-product-claim.api';

export interface DeleteProductClaimDialogProps extends BaseDialogProps {
  ProductClaimId: string;
}

export default function DeleteProductClaimDialog({
  dialog,
  ProductClaimId,
}: DeleteProductClaimDialogProps) {
  const { closeDeleteProductClaimDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const deleteProductClaimMutation = useMutation({
    ...deleteProductClaimMutationOptions(),
    onSuccess: () => {
      toast.success('Klaim produk berhasil dihapus');
      queryClient.invalidateQueries({
        queryKey: [GET_LIST_KLAIM_PRODUK_QUERY_KEY],
      });
      closeDeleteProductClaimDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menghapus klaim produk';
      toast.error(message);
    },
  });

  const isPending = deleteProductClaimMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeDeleteProductClaimDialog();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus</AlertDialogTitle>
          <AlertDialogDescription>
            Apakah Anda yakin ingin menghapus klaim produk ini?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Batal</AlertDialogCancel>
          <AlertDialogAction
            onClick={() =>
              deleteProductClaimMutation.mutate({
                klaim_produk_id: ProductClaimId,
              })
            }
            disabled={isPending}
          >
            {isPending ? 'Menghapus...' : 'Hapus'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
